import React, { useState } from 'react';
import {
	LoadingOverlay,
	TextInput,
	Title,
	Button,
	Text,
	Notification,
	useMantineTheme,
	Box,
} from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import { AppPaths } from '@/configs/appPaths';
import { SubmitStatus } from '../login/Login';
import { ErrorCode } from '@/types/types';
import { useContent } from '@/providers/ContentProvider';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import styles from './ResetPassword.module.scss';

const initialValues = {
	email: '',
};

const baseSlug = 'resetPassword.';

export const ResetPassword: React.FC = () => {
	const { other } = useMantineTheme();

	const [status, setStatus] = useState(SubmitStatus.THROTTLE);

	const { getErrorMessage, getContent } = useContent();

	const form = useForm({
		initialValues,
		validate: {
			email: isEmail(getErrorMessage(ErrorCode.INVALID_EMAIL)),
		},
	});

	const handleSubmit = async () => {
		setStatus(SubmitStatus.LOADING);
		const response = await UnauthenticatedApi.requestResetPassword(
			form.values.email
		);
		form.reset();

		if (response === SubmitStatus.SUCCESS) {
			setStatus(SubmitStatus.SUCCESS);
			return;
		}

		form.setFieldError('email', getErrorMessage(ErrorCode.USER_NOT_FOUND));
		setStatus(SubmitStatus.ERROR);
	};

	return (
		<div className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={form.onSubmit(handleSubmit)}>
				<LoadingOverlay visible={status === SubmitStatus.LOADING} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}title`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}description`)}
				</Text>
				{status === SubmitStatus.SUCCESS && (
					<Notification
						{...other.notificationProps.success}
						mb="lg"
						onClose={() => setStatus(SubmitStatus.THROTTLE)}
					>
						<Text size="md" my="sm">
							{getContent(`${baseSlug}successMessage`)}
						</Text>
					</Notification>
				)}
				{status !== SubmitStatus.SUCCESS && (
					<>
						<TextInput
							{...form.getInputProps('email')}
							required
							type="email"
							placeholder={getContent(`emailPlaceholder`)}
							mb="lg"
							size="lg"
						/>
						<Button w="100%" type="submit" mb="lg">
							{getContent(`${baseSlug}submitButton`)}
						</Button>
					</>
				)}
				<Box ta="center" c="dimmed">
					{getContent(`${baseSlug}goBackToLogin`, {
						links: {
							$LOGIN: {
								to: `/${AppPaths.LOGIN}`,
								className: 'injected-anchor',
							},
						},
					})}
				</Box>
			</form>
		</div>
	);
};
